/**
 * Auto-ajuste de imágenes según el uso (kind).
 *
 * Cada kind tiene un preset óptimo (target ratio + max dimensions + fit strategy):
 *   hero        → 1920×900 cover (panorámico para hero de landing)
 *   keypad      → 800×800 contain (producto centrado con padding)
 *   product     → 1000×1000 contain
 *   lifestyle   → 1600×1200 cover (4:3 ambiente)
 *   ambient     → 1920×1080 cover (16:9 hero secundario)
 *   spec_diagram→ 1400×width contain (preserva legibilidad)
 *
 * Usa Canvas API para resize/crop client-side antes de upload — sin dependencia server.
 */

export type ImageKind = 'hero' | 'keypad' | 'product' | 'lifestyle' | 'ambient' | 'spec_diagram' | 'video';

export type ImagePreset = {
  width: number;
  height: number;
  fit: 'cover' | 'contain';
  background: string; // color cuando contain deja espacios
  quality: number;    // 0..1 para JPEG
  description: string;
};

export const PRESETS: Record<ImageKind, ImagePreset> = {
  hero:         { width: 1920, height: 900,  fit: 'cover',   background: '#0e0e0e', quality: 0.85, description: 'Panorámico 1920×900 para hero · cover crop al centro' },
  keypad:       { width: 800,  height: 800,  fit: 'contain', background: '#F4F5F5', quality: 0.92, description: '800×800 con padding · keypad/dimmer aislado' },
  product:      { width: 1000, height: 1000, fit: 'contain', background: '#FFFFFF', quality: 0.92, description: '1000×1000 fondo blanco · producto plain' },
  lifestyle:    { width: 1600, height: 1200, fit: 'cover',   background: '#0e0e0e', quality: 0.82, description: '1600×1200 (4:3) · ambiente cover' },
  ambient:      { width: 1920, height: 1080, fit: 'cover',   background: '#0e0e0e', quality: 0.82, description: '1920×1080 (16:9) · hero secundario' },
  spec_diagram: { width: 1400, height: 0,    fit: 'contain', background: '#FFFFFF', quality: 0.95, description: '1400px wide · diagrama (preserva legibilidad)' },
  video:        { width: 0,    height: 0,    fit: 'cover',   background: '#000',    quality: 1,    description: 'Sin procesamiento — video sube intacto' },
};

export type AnalyzedImage = {
  blob: Blob;
  width: number;
  height: number;
  aspectRatio: number;
  isPortrait: boolean;
  isPanoramic: boolean;
  isSquare: boolean;
  bytes: number;
};

/**
 * Carga un archivo o URL y devuelve dimensiones + blob original.
 */
export async function analyzeImage(source: File | string): Promise<AnalyzedImage> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = async () => {
      let blob: Blob;
      if (source instanceof File) blob = source;
      else {
        const res = await fetch(source);
        blob = await res.blob();
      }
      const aspectRatio = img.width / img.height;
      resolve({
        blob,
        width: img.width,
        height: img.height,
        aspectRatio,
        isPortrait: aspectRatio < 0.85,
        isPanoramic: aspectRatio > 1.7,
        isSquare: aspectRatio >= 0.85 && aspectRatio <= 1.2,
        bytes: blob.size,
      });
    };
    img.onerror = () => reject(new Error('No se pudo cargar la imagen'));
    img.src = source instanceof File ? URL.createObjectURL(source) : source;
  });
}

/**
 * Sugiere el mejor kind para una imagen basado en sus dimensiones.
 */
export function suggestKind(img: AnalyzedImage): { kind: ImageKind; confidence: 'high' | 'medium' | 'low'; reason: string } {
  // Muy panorámica → hero
  if (img.aspectRatio >= 2.0 && img.width >= 1400) {
    return { kind: 'hero', confidence: 'high', reason: 'Imagen panorámica grande — ideal como hero' };
  }
  if (img.aspectRatio >= 1.6 && img.width >= 1200) {
    return { kind: 'ambient', confidence: 'high', reason: 'Imagen 16:9 — perfecta como ambiente o hero secundario' };
  }
  // Cuadrada o casi-cuadrada y de tamaño medio → keypad/producto
  if (img.isSquare && img.width <= 1200) {
    return { kind: 'keypad', confidence: 'high', reason: 'Imagen cuadrada — formato típico de keypad/producto aislado' };
  }
  // 4:3 mediana → lifestyle
  if (img.aspectRatio >= 1.25 && img.aspectRatio <= 1.5 && img.width >= 1000) {
    return { kind: 'lifestyle', confidence: 'high', reason: '4:3 — formato típico de foto en contexto / lifestyle' };
  }
  // Vertical → mala señal para landing
  if (img.isPortrait) {
    return { kind: 'product', confidence: 'low', reason: '⚠ Imagen vertical — no es ideal para landing. Mejor usar como producto o evitar.' };
  }
  // Default
  return { kind: 'product', confidence: 'medium', reason: 'Proporciones intermedias — usable como producto' };
}

/**
 * Aplica el preset del kind a la imagen: resize + crop + recompresión.
 * Devuelve un nuevo Blob optimizado listo para upload.
 */
export async function processImage(file: File, kind: ImageKind): Promise<Blob> {
  if (kind === 'video') return file; // video pasa sin procesar
  if (kind === 'spec_diagram') return resizeToWidth(file, PRESETS.spec_diagram.width, PRESETS.spec_diagram.quality);

  const preset = PRESETS[kind];
  return resizeAndCrop(file, preset);
}

async function resizeToWidth(file: File, targetWidth: number, quality: number): Promise<Blob> {
  const img = await loadImage(file);
  if (img.width <= targetWidth) {
    return file; // ya es chica, no degradar
  }
  const scale = targetWidth / img.width;
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = Math.round(img.height * scale);
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvasToBlob(canvas, file.type, quality);
}

async function resizeAndCrop(file: File, preset: ImagePreset): Promise<Blob> {
  const img = await loadImage(file);
  const canvas = document.createElement('canvas');
  canvas.width = preset.width;
  canvas.height = preset.height;
  const ctx = canvas.getContext('2d')!;

  // Fondo
  ctx.fillStyle = preset.background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const srcRatio = img.width / img.height;
  const dstRatio = preset.width / preset.height;

  if (preset.fit === 'cover') {
    // Llenar canvas, cropear el sobrante
    let sx = 0, sy = 0, sw = img.width, sh = img.height;
    if (srcRatio > dstRatio) {
      // Source más ancha → cropear horizontal
      sw = img.height * dstRatio;
      sx = (img.width - sw) / 2;
    } else {
      // Source más alta → cropear vertical (usualmente desde arriba, no centro)
      sh = img.width / dstRatio;
      sy = Math.max(0, (img.height - sh) * 0.3); // 30% from top, más natural
    }
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
  } else {
    // contain: dejar margen, centrar
    let dw = canvas.width, dh = canvas.height, dx = 0, dy = 0;
    if (srcRatio > dstRatio) {
      dh = canvas.width / srcRatio;
      dy = (canvas.height - dh) / 2;
    } else {
      dw = canvas.height * srcRatio;
      dx = (canvas.width - dw) / 2;
    }
    // padding leve dentro del contain
    const padding = 0.04;
    dx += canvas.width * padding;
    dy += canvas.height * padding;
    dw -= canvas.width * padding * 2;
    dh -= canvas.height * padding * 2;
    ctx.drawImage(img, dx, dy, dw, dh);
  }

  // Exportar como JPEG por default (más chico que PNG); WebP si el navegador lo soporta
  const mime = supportsWebP() ? 'image/webp' : 'image/jpeg';
  return canvasToBlob(canvas, mime, preset.quality);
}

function loadImage(file: File | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error('canvas.toBlob falló')),
      mime,
      quality,
    );
  });
}

function supportsWebP(): boolean {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 1;
  return canvas.toDataURL('image/webp').startsWith('data:image/webp');
}

/**
 * Helper: cambia la extensión del filename para que coincida con el mime de salida.
 */
export function pickFilename(original: string, kind: ImageKind): string {
  const base = original.replace(/\.[a-z0-9]{1,5}$/i, '');
  if (kind === 'video') return original;
  const ext = supportsWebP() ? 'webp' : 'jpg';
  return `${base}.${ext}`;
}
